import { API_BASE_URL, API_ROUTES, HTTP_STATUS } from '../constants/api'
import {
  ACCESS_TOKEN_REFRESH_SKEW_MS,
  NOTIFICATION_RECONNECT_MAX_MS,
  NOTIFICATION_RECONNECT_MS,
  type InboxNotification,
} from '../constants/notifications'
import { refreshAccessToken } from './api-client'
import { getAccessToken } from './auth-storage'
import { mapInboxNotification } from './overview'

export type NotificationStreamHandlers = {
  onNotification: (item: InboxNotification) => void
  onUnreadCount: (count: number) => void
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const timer = window.setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer)
        resolve()
      },
      { once: true },
    )
  })
}

function parseSseBlock(block: string): { event: string; data: string } | null {
  let event = 'message'
  const dataLines: string[] = []
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (!dataLines.length) return null
  return { event, data: dataLines.join('\n') }
}

function handleSseBlock(block: string, handlers: NotificationStreamHandlers) {
  const frame = parseSseBlock(block)
  if (!frame || !frame.data) return
  let parsed: unknown
  try {
    parsed = JSON.parse(frame.data)
  } catch {
    return
  }
  if (frame.event === 'notification' && parsed && typeof parsed === 'object') {
    const item = mapInboxNotification(parsed as Record<string, unknown>)
    if (item) handlers.onNotification(item)
    return
  }
  if (
    frame.event === 'unread-count' &&
    parsed &&
    typeof parsed === 'object' &&
    'count' in parsed
  ) {
    const count = Number((parsed as { count: unknown }).count)
    if (Number.isFinite(count)) handlers.onUnreadCount(count)
  }
}

type StreamResult = 'retry' | 'stop'

function accessTokenExpiryMs(token: string): number | null {
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const json = JSON.parse(atob(padded)) as { exp?: unknown }
    return typeof json.exp === 'number' ? json.exp * 1000 : null
  } catch {
    return null
  }
}

async function currentAccessToken(): Promise<string | null> {
  const token = getAccessToken()
  if (!token) return null
  const expiry = accessTokenExpiryMs(token)
  if (expiry != null && expiry - Date.now() <= ACCESS_TOKEN_REFRESH_SKEW_MS) {
    return refreshAccessToken()
  }
  return token
}

function openStream(token: string, signal: AbortSignal): Promise<Response> {
  return fetch(`${API_BASE_URL}${API_ROUTES.notificationsStream}`, {
    cache: 'no-store',
    headers: {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    signal,
  })
}

function tokenNeedsRefresh(token: string | null): boolean {
  if (!token) return true
  const expiry = accessTokenExpiryMs(token)
  if (expiry == null) return true
  return expiry - Date.now() <= ACCESS_TOKEN_REFRESH_SKEW_MS
}

function bindAbort(parent: AbortSignal): {
  signal: AbortSignal
  abort: () => void
  dispose: () => void
} {
  const inner = new AbortController()
  const onParentAbort = () => inner.abort()
  if (parent.aborted) inner.abort()
  else parent.addEventListener('abort', onParentAbort, { once: true })
  return {
    signal: inner.signal,
    abort: () => inner.abort(),
    dispose: () => parent.removeEventListener('abort', onParentAbort),
  }
}

async function recoverFromStreamError(signal: AbortSignal): Promise<StreamResult> {
  if (signal.aborted) return 'stop'
  if (!tokenNeedsRefresh(getAccessToken())) return 'retry'
  const refreshed = await refreshAccessToken()
  if (!refreshed || signal.aborted) return 'stop'
  return 'retry'
}

async function readStream(
  signal: AbortSignal,
  handlers: NotificationStreamHandlers,
): Promise<StreamResult> {
  let token = await currentAccessToken()
  if (!token || signal.aborted) return 'stop'

  const readerAbort = bindAbort(signal)
  const expiry = accessTokenExpiryMs(token)
  let expiryTimer: number | undefined
  if (expiry != null) {
    const delay = expiry - Date.now() - ACCESS_TOKEN_REFRESH_SKEW_MS
    if (delay > 0) {
      expiryTimer = window.setTimeout(() => readerAbort.abort(), delay)
    }
  }

  try {
    let response = await openStream(token, readerAbort.signal)
    if (response.status === HTTP_STATUS.unauthorized) {
      token = await refreshAccessToken()
      if (!token || signal.aborted) return 'stop'
      response = await openStream(token, readerAbort.signal)
      if (response.status === HTTP_STATUS.unauthorized) return 'stop'
    }
    if (signal.aborted) return 'stop'
    if (readerAbort.signal.aborted) return 'retry'
    if (!response.ok || !response.body) return 'retry'

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (!readerAbort.signal.aborted) {
      const { value, done } = await reader.read()
      if (done) return signal.aborted ? 'stop' : 'retry'
      buffer += decoder.decode(value, { stream: true })
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop() ?? ''
      for (const block of blocks) handleSseBlock(block, handlers)
    }
    return signal.aborted ? 'stop' : 'retry'
  } catch {
    if (signal.aborted || readerAbort.signal.aborted) {
      return signal.aborted ? 'stop' : 'retry'
    }
    return recoverFromStreamError(signal)
  } finally {
    if (expiryTimer != null) window.clearTimeout(expiryTimer)
    readerAbort.dispose()
  }
}

export function startNotificationStream(
  handlers: NotificationStreamHandlers,
): () => void {
  const controller = new AbortController()
  void (async () => {
    let delay = NOTIFICATION_RECONNECT_MS
    while (!controller.signal.aborted) {
      let result: StreamResult = 'retry'
      try {
        result = await readStream(controller.signal, handlers)
        delay = NOTIFICATION_RECONNECT_MS
      } catch {
        if (controller.signal.aborted) return
        result = await recoverFromStreamError(controller.signal)
      }
      if (result === 'stop' || controller.signal.aborted) return
      await wait(delay, controller.signal)
      delay = Math.min(delay * 2, NOTIFICATION_RECONNECT_MAX_MS)
    }
  })()
  return () => controller.abort()
}
